import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, CreditCard, DollarSign, Users, Repeat, Gift, Building2, User, Download } from "lucide-react";

const fmtMoney = (cents, currency) => {
  const n = (cents || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "aud").toUpperCase(),
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const isoDay = (d) => d.toISOString().slice(0, 10);

const STATUS_BADGE = {
  active: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  trialing: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  past_due: "border-rose-500/40 bg-rose-500/10 text-rose-300",
  canceled: "border-slate-600 bg-slate-700/40 text-slate-400",
  manual: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  inactive: "border-slate-600 bg-slate-700/40 text-slate-400",
};

const Stat = ({ label, value, sub, Icon, testid }) => (
  <div className="rounded-xl border border-slate-800 bg-[#0e1726] p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      {Icon && <Icon className="w-4 h-4 text-[#5ec0ff]" />}
    </div>
    <div className="text-3xl font-bold text-white mt-1">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

const PRESETS = [
  { id: "all", label: "All time" },
  { id: "month", label: "This month" },
  { id: "30", label: "Last 30 days" },
  { id: "year", label: "This year" },
];

const csvEscape = (v) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function AdminSubscribers() {
  const [data, setData] = useState(null);
  const [preset, setPreset] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    api.get("/api/admin/subscribers").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  const applyPreset = (p) => {
    const now = new Date();
    let f = "", t = "";
    if (p === "month") { f = isoDay(new Date(now.getFullYear(), now.getMonth(), 1)); t = isoDay(now); }
    else if (p === "30") { const d = new Date(now); d.setDate(d.getDate() - 30); f = isoDay(d); t = isoDay(now); }
    else if (p === "year") { f = isoDay(new Date(now.getFullYear(), 0, 1)); t = isoDay(now); }
    setFrom(f); setTo(t); setPreset(p);
  };

  const rows = useMemo(() => (data && data.subscribers) || [], [data]);
  const filtered = useMemo(() => {
    const within = (iso) => {
      if (!from && !to) return true;
      if (!iso) return false;
      const d = String(iso).slice(0, 10);
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    };
    return rows.filter((s) => within(s.started_at));
  }, [rows, from, to]);
  const filteredIncome = filtered.reduce((sum, s) => sum + (s.amount_paid_cents || 0), 0);
  const filterActive = !!(from || to);

  const t = data && data.totals;

  const downloadCsv = () => {
    const header = ["Type", "Name", "Email", "Plan", "Started on", "Status", "Members", "Seats", "Income", "Currency"];
    const lines = filtered.map((s) => [
      s.type, s.name, s.email || "", s.plan,
      s.started_at ? String(s.started_at).slice(0, 10) : "",
      s.status || "",
      s.type === "team" ? (s.members ?? "") : "",
      s.type === "team" ? (s.seats ?? "") : "",
      ((s.amount_paid_cents || 0) / 100).toFixed(2),
      (s.currency || data.currency || "aud").toUpperCase(),
    ]);
    const csv = [header, ...lines].map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscribers_${isoDay(new Date())}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-subscribers-page">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#5ec0ff] font-semibold mb-1">
        <CreditCard className="w-3.5 h-3.5" /> Subscribers
      </div>
      <h1 className="text-2xl font-bold text-white">Subscribers &amp; revenue</h1>
      <p className="text-sm text-slate-400 mt-1">
        Paying subscribers, when each started, and the actual income collected via Stripe.
      </p>

      {data === null ? (
        <div className="flex items-center justify-center gap-2 text-slate-500 text-sm py-16">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading subscribers…
        </div>
      ) : data === false ? (
        <div className="text-center text-slate-500 py-16 text-sm">Couldn’t load subscribers.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <Stat label="Subscribers" value={t.subscribers} sub={`${t.pro_active} Pro · ${t.teams_active} team${t.teams_active === 1 ? "" : "s"}`} Icon={Users} testid="metric-subscribers" />
            <Stat label="Total income" value={fmtMoney(t.total_income_cents, data.currency)} sub="collected to date" Icon={DollarSign} testid="metric-total-income" />
            <Stat label="MRR" value={fmtMoney(t.mrr_cents, data.currency)} sub="recurring / month" Icon={Repeat} testid="metric-mrr" />
            <Stat label="Complimentary" value={t.comp} sub="free granted access" Icon={Gift} testid="metric-comp" />
          </div>

          {!data.stripe_enabled && (
            <p className="text-[11px] text-amber-400 mt-4" data-testid="stripe-disabled-note">
              Stripe is not configured — income figures are unavailable.
            </p>
          )}

          {/* date-range filter + CSV export */}
          <div className="mt-8 flex flex-col lg:flex-row lg:items-end gap-4 flex-wrap" data-testid="subscribers-filter">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Started between</div>
              <div className="flex flex-wrap items-center gap-1.5">
                {PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => applyPreset(p.id)}
                    data-testid={`range-preset-${p.id}`}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                      preset === p.id
                        ? "border-[#0047AB] bg-[#0047AB]/20 text-white"
                        : "border-slate-700 text-slate-400 hover:border-slate-600"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">From</label>
                <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }}
                  data-testid="range-from" className="bg-[#0b1220] border border-slate-700 text-slate-200 text-sm rounded-md px-2.5 py-1.5 focus:border-[#5ec0ff] outline-none" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">To</label>
                <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPreset("custom"); }}
                  data-testid="range-to" className="bg-[#0b1220] border border-slate-700 text-slate-200 text-sm rounded-md px-2.5 py-1.5 focus:border-[#5ec0ff] outline-none" />
              </div>
              {filterActive && (
                <button type="button" onClick={() => { setFrom(""); setTo(""); setPreset("all"); }}
                  data-testid="range-clear" className="text-xs text-slate-400 hover:text-white px-2 py-1.5">
                  Clear
                </button>
              )}
            </div>
            <div className="lg:ml-auto">
              <Button onClick={downloadCsv} disabled={filtered.length === 0} data-testid="download-csv-btn"
                className="bg-[#0047AB] hover:bg-[#003380] text-white">
                <Download className="w-4 h-4 mr-1.5" /> Download CSV
              </Button>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-slate-800 bg-[#0e1726] overflow-hidden" data-testid="subscribers-table">
            <div className="px-4 py-3 border-b border-slate-800 text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center justify-between">
              <span>Subscriber detail</span>
              <span className="text-slate-600 normal-case tracking-normal" data-testid="subscribers-filter-summary">
                {filterActive
                  ? `${filtered.length} of ${rows.length} · ${fmtMoney(filteredIncome, data.currency)} in range`
                  : `${rows.length} record${rows.length === 1 ? "" : "s"}`}
              </span>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center text-slate-500 py-12 text-sm" data-testid="subscribers-empty">
                {rows.length === 0
                  ? "No subscribers yet. Pro sign-ups and active teams will appear here."
                  : "No subscribers started in this date range."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
                      <th className="text-left font-semibold px-4 py-2.5">Subscriber</th>
                      <th className="text-left font-semibold px-4 py-2.5">Plan</th>
                      <th className="text-left font-semibold px-4 py-2.5">Started on</th>
                      <th className="text-left font-semibold px-4 py-2.5">Status</th>
                      <th className="text-right font-semibold px-4 py-2.5">Income</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {filtered.map((s, i) => (
                      <tr key={s.id} className="hover:bg-slate-800/30" data-testid={`subscriber-row-${i}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <span className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${s.type === "team" ? "bg-sky-500/15 text-sky-300" : "bg-emerald-500/15 text-emerald-300"}`}>
                              {s.type === "team" ? <Building2 className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
                            </span>
                            <div className="min-w-0">
                              <div className="text-slate-100 font-medium truncate" data-testid={`subscriber-name-${i}`}>{s.name}</div>
                              <div className="text-[11px] text-slate-500 truncate">
                                {s.type === "team" ? `${s.members ?? 0}/${s.seats ?? "—"} seats used` : s.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-slate-300">{s.plan}</span>
                          {s.cancel_at_period_end && (
                            <span className="block text-[10px] text-rose-400">cancels at period end</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-300 font-mono-clinical whitespace-nowrap" data-testid={`subscriber-started-${i}`}>
                          {fmtDate(s.started_at)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full border ${STATUS_BADGE[s.status] || STATUS_BADGE.inactive}`}>
                            {s.status || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono-clinical font-semibold text-white whitespace-nowrap" data-testid={`subscriber-income-${i}`}>
                          {fmtMoney(s.amount_paid_cents, s.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-700 bg-slate-800/30">
                      <td className="px-4 py-3 text-slate-400 text-xs uppercase tracking-widest font-semibold" colSpan={4}>
                        {filterActive ? "Income collected (in range)" : "Total income collected"}
                      </td>
                      <td className="px-4 py-3 text-right font-mono-clinical font-bold text-emerald-300 whitespace-nowrap" data-testid="subscribers-total-income">
                        {fmtMoney(filterActive ? filteredIncome : t.total_income_cents, data.currency)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
